import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import {
    Stack,
    ToggleButtonGroup,
    ToggleButton,
    Fade,
    Typography,
    TextField,
    Box,
    Slider,
} from "@mui/material"
import FormControl from "@mui/material/FormControl"
import FormControlLabel from "@mui/material/FormControlLabel"
import Radio from "@mui/material/Radio"
import RadioGroup from "@mui/material/RadioGroup"
import Grid from "@mui/material/Unstable_Grid2"
import { ReactElement, SyntheticEvent, useState } from "react"
import { useNavigate, useParams, useSearchParams } from "react-router-dom"

import AppButton from "../../components/buttons/appButton"
import AppLoadingButton from "../../components/buttons/appLoadingButton"
import FilterPanelFrame from "../../components/fitlerPanel/filterPanelFrame"
import FilterTabBarFrame from "../../components/fitlerPanel/filterTabBarFrame"
import SelectedFilterTab from "../../components/fitlerPanel/selectedFilterTab"
import AppControlledSelect from "../../components/inputs/controlled/appControlledSelect"
import {
    LCT_PUBLIC_STATUS,
    LCT_STATUS_MAPPINGS,
} from "../../components/popUps/assessDevice/assessDeviceForm"
import AppPaper from "../../components/surfaces/appPaper"
import DemandSearchResults from "../../components/tables/demandSearchResults"
import DisplayIf from "../../components/wrappers/displayIf"
import { Datasets, LCTOnlyDatasets } from "../../constants/enums"
import {
    DEMAND_DEVICE_SYSTEM_REF_KW,
    DEMAND_KW,
    FILTER_PANEL_SLIDE_MS,
    GENERATION_KW,
} from "../../constants/generalConstants"
import { useDemandSearchURLFilterParams } from "../../hooks/useDemandDeriveFilterValuesFromUrl"
import { useGetUserData } from "../../hooks/useGetUserData"
import { GEN_DEVICE_SEARCH } from "../../router/routes"
import useDemandFilterReferenceData from "../../services/middleware/hooks/useDemandFilterReference"
import useSearchDemandDevices, {
    DEMAND_PAGE_SIZE,
} from "../../services/middleware/hooks/useMultiFetcherSearchDemandDevices"
import {
    DEM_MANUFACTURER_REF_KEY,
    DEM_MANUFACTURER_URL_PARAM,
    DEM_MODEL_URL_PARAM,
    DEM_PHASE_REF_KEY,
    DEM_PHASE_URL_PARAM,
    DEM_STATUS_REF_KEY,
    DEM_STATUS_URL_PARAM,
    DEM_VOLTAGE_REF_KEY,
    DEM_VOLTAGE_URL_PARAM,
} from "../../services/middleware/mappers/demandFilterReferenceMapper"
import {
    BUTTON_RADIUS_MULTIPLIER,
    GRID_SPACING,
} from "../../themes/defaultThemeParams"

export const MAX_DEMAND = 400

export type TFilterSelect = Record<string, string | number> | null

/**
 * Webpage for low carbon technology database search interface for demand
 * devices
 * @example
 * <DemandDeviceSearch />
 * */
const DemandDeviceSearch = (): ReactElement => {
    // API calls
    const { dataset } = useParams()
    const navigate = useNavigate()
    const { isEnaSysAdmin, isManuApproved } = useGetUserData()
    const [searchParams, setSearchParams] = useSearchParams()
    const {
        data,
        isError,
        isLoading,
        isValidating,
        page,
        setPage,
        isFinalPage,
        canAccessProtected,
    } = useSearchDemandDevices(searchParams, dataset as LCTOnlyDatasets)

    const { referenceData, isLoading: refDataLoading } =
        useDemandFilterReferenceData(dataset as LCTOnlyDatasets)

    const {
        complianceStatus,
        demandLb,
        demandUb,
        manufacturer,
        modelRef,
        phase,
        orderDirection,
        orderBy,
        systemRef,
        voltage,
        clearAllFilters,
        setOrderBy,
        setFilterValue,
        setFilterFromTextfield,
        setFilterFromSlider,
        toggleDataset,
    } = useDemandSearchURLFilterParams(searchParams, setSearchParams)

    // Local states only to be used sparingly - where possible rely on URL
    // params - this is to reduce renders
    const [localSystemRef, setLocalSystemRef] = useState<string>(systemRef)
    const [localModelRef, setLocalModelRef] = useState<string>(modelRef)
    const [localSliderValue, setLocalSliderValue] = useState<[number, number]>([
        demandLb,
        demandUb,
    ])

    if (isError) {
        return (
            <AppPaper>
                <h1>Error</h1>
            </AppPaper>
        )
    }

    /**
     * Called when slider is moved is committed. Updates slider local state
     * but not URL params.
     * @param e_ - MUI slider event, not used
     * @param newValue - array of upper and lower slider value
     */
    const onSlide = (e_: Event, newValue: number | number[]): void => {
        setLocalSliderValue(newValue as [number, number])
    }

    const title = (isManuTitle: boolean): string => {
        if (dataset === LCTOnlyDatasets.EVAC) {
            return isManuTitle
                ? "Electric Vehicle AC Devices"
                : "ENA Electric Vehicle AC Register"
        }
        if (dataset === LCTOnlyDatasets.EVDC) {
            return isManuTitle
                ? "Electric Vehicle DC Devices"
                : "ENA Electric Vehicle DC Register"
        }
        if (dataset === LCTOnlyDatasets.HP) {
            return isManuTitle ? "Heat Pump Devices" : "ENA Heat Pump Register"
        }

        return "Invalid dataset"
    }

    const isManuTitle = isManuApproved && !isEnaSysAdmin

    return (
        <Grid container spacing={GRID_SPACING}>
            <FilterTabBarFrame
                isValidating={isValidating}
                dataset={dataset as Datasets}
                titleToDisplayInBaseColour={isManuTitle ? "My" : "Search the"}
                titleToDisplayInPrimaryColour={title(isManuTitle)}
            >
                <SelectedFilterTab
                    name="Manufacturer"
                    value={manufacturer}
                    onDelete={() =>
                        setFilterValue(null, DEM_MANUFACTURER_URL_PARAM)
                    }
                />
                <SelectedFilterTab
                    name="Model Reference"
                    value={modelRef}
                    onDelete={() => {
                        setLocalModelRef("")
                        setFilterFromTextfield("", DEM_MODEL_URL_PARAM)
                    }}
                />
                <SelectedFilterTab
                    name="Phases"
                    value={phase}
                    onDelete={() => setFilterValue(null, DEM_PHASE_URL_PARAM)}
                />
                <SelectedFilterTab
                    name="Min Demand"
                    value={localSliderValue[0]}
                    show={demandLb !== 0}
                    onDelete={() => {
                        const newValues: [number, number] = [0, demandUb]
                        setLocalSliderValue(newValues)
                        setFilterFromSlider(new Event("void"), newValues)
                    }}
                />
                <SelectedFilterTab
                    name="Max Demand"
                    value={localSliderValue[1]}
                    show={demandUb !== MAX_DEMAND}
                    onDelete={() => {
                        const newValues: [number, number] = [
                            demandLb,
                            MAX_DEMAND,
                        ]
                        setLocalSliderValue(newValues)
                        setFilterFromSlider(new Event("void"), newValues)
                    }}
                />
                <SelectedFilterTab
                    name="Status"
                    value={complianceStatus}
                    onDelete={() => setFilterValue(null, DEM_STATUS_REF_KEY)}
                />
                <SelectedFilterTab
                    name="Voltage"
                    value={voltage}
                    onDelete={() => setFilterValue(null, DEM_VOLTAGE_URL_PARAM)}
                />
                <SelectedFilterTab
                    name="System Ref"
                    value={systemRef}
                    onDelete={() => {
                        setLocalSystemRef("")
                        setFilterFromTextfield("", DEMAND_DEVICE_SYSTEM_REF_KW)
                    }}
                />

                <Fade
                    in={
                        !!manufacturer ||
                        !!modelRef ||
                        !!phase ||
                        !!voltage ||
                        !!systemRef ||
                        !!demandLb ||
                        !!complianceStatus ||
                        demandUb !== MAX_DEMAND
                    }
                    timeout={FILTER_PANEL_SLIDE_MS}
                >
                    <AppButton
                        variant="outlined"
                        onClick={clearAllFilters}
                        endIcon={<ClearOutlinedIcon />}
                    >
                        Clear All
                    </AppButton>
                </Fade>
            </FilterTabBarFrame>

            <Grid xs>
                <AppPaper>
                    <Stack spacing={GRID_SPACING}>
                        <DemandSearchResults
                            searchResults={data?.data}
                            orderBy={orderBy}
                            orderDirection={orderDirection}
                            setOrderBy={setOrderBy}
                            isLoading={isLoading}
                        />
                        {data && (
                            <Stack
                                alignItems="center"
                                justifyContent="space-between"
                                direction={{ xs: "column", sm: "row" }}
                                width="100%"
                                spacing={1}
                            >
                                <div />
                                {!isFinalPage(page, data.totalResults) && (
                                    <AppLoadingButton
                                        color="secondary"
                                        endIcon={<MoreHorizIcon />}
                                        loading={isValidating}
                                        loadingPosition="end"
                                        onClick={() => setPage(page + 1)}
                                        sx={{
                                            width: { xs: "100%", md: "20%" },
                                            mt: "6px",
                                        }}
                                    >
                                        See More
                                    </AppLoadingButton>
                                )}
                                <Typography>
                                    {`Showing ${
                                        isFinalPage(page, data.totalResults)
                                            ? data.totalResults
                                            : page * DEMAND_PAGE_SIZE
                                    } devices of ${data.totalResults}`}
                                </Typography>
                            </Stack>
                        )}
                    </Stack>
                </AppPaper>
            </Grid>
            <FilterPanelFrame>
                <ToggleButtonGroup
                    color="primary"
                    fullWidth
                    value={DEMAND_KW}
                    size="small"
                    exclusive
                    onChange={() => navigate(GEN_DEVICE_SEARCH)}
                >
                    <ToggleButton
                        value={DEMAND_KW}
                        disabled
                        sx={{
                            borderRadius: BUTTON_RADIUS_MULTIPLIER,
                            textTransform: "none",
                        }}
                    >
                        {DEMAND_KW}
                    </ToggleButton>
                    <ToggleButton
                        value={GENERATION_KW}
                        sx={{
                            borderRadius: BUTTON_RADIUS_MULTIPLIER,
                            textTransform: "none",
                        }}
                    >
                        {GENERATION_KW}
                    </ToggleButton>
                </ToggleButtonGroup>
                <FormControl>
                    <RadioGroup value={dataset} onChange={toggleDataset}>
                        <FormControlLabel
                            value={LCTOnlyDatasets.HP}
                            control={<Radio />}
                            label="Heat Pumps"
                        />
                        <FormControlLabel
                            value={LCTOnlyDatasets.EVAC}
                            control={<Radio />}
                            label="EV Charge Points AC"
                        />
                        <FormControlLabel
                            value={LCTOnlyDatasets.EVDC}
                            control={<Radio />}
                            label="EV Charge Points DC"
                        />
                    </RadioGroup>
                </FormControl>
                <DisplayIf condition={!isManuApproved && !isEnaSysAdmin}>
                    <AppControlledSelect
                        fullWidth
                        value={manufacturer}
                        loading={refDataLoading}
                        label="Manufacturer"
                        options={
                            referenceData?.[DEM_MANUFACTURER_REF_KEY] || []
                        }
                        setValue={(
                            e_: SyntheticEvent<Element, Event>,
                            nv: string | number | null
                        ) => setFilterValue(nv, DEM_MANUFACTURER_URL_PARAM)}
                    />
                </DisplayIf>
                <TextField
                    value={localModelRef}
                    placeholder="Type to search..."
                    label="Model Reference"
                    onChange={(event) => {
                        setLocalModelRef(event.target.value)
                        setFilterFromTextfield(
                            event.target.value,
                            DEM_MODEL_URL_PARAM
                        )
                    }}
                />
                <AppControlledSelect
                    fullWidth
                    value={phase}
                    loading={refDataLoading}
                    label="Phases"
                    options={
                        referenceData?.[DEM_PHASE_REF_KEY] as readonly (
                            | string
                            | number
                        )[]
                    }
                    setValue={(
                        e_: SyntheticEvent<Element, Event>,
                        nv: string | number | null
                    ) => setFilterValue(nv, DEM_PHASE_URL_PARAM)}
                />
                <Box>
                    <Typography variant="body1" gutterBottom>
                        {`Max Device Demand
                        : ${localSliderValue[0]} - ${localSliderValue[1]}${
                            localSliderValue[1] === MAX_DEMAND ? "+" : ""
                        } A`}
                    </Typography>
                    <Slider
                        value={localSliderValue}
                        max={MAX_DEMAND}
                        onChange={onSlide}
                        onChangeCommitted={setFilterFromSlider}
                        valueLabelDisplay="auto"
                        getAriaLabel={(index) => `capacitySlider${index}`}
                    />
                </Box>
                <AppControlledSelect
                    fullWidth
                    value={complianceStatus}
                    loading={refDataLoading}
                    label="Compliance Status"
                    options={
                        canAccessProtected
                            ? Object.keys(LCT_STATUS_MAPPINGS)
                            : LCT_PUBLIC_STATUS
                    }
                    setValue={(
                        e_: SyntheticEvent<Element, Event>,
                        nv: string | number | null
                    ) => setFilterValue(nv, DEM_STATUS_URL_PARAM)}
                />
                <AppControlledSelect
                    fullWidth
                    value={voltage}
                    loading={refDataLoading}
                    label="Voltages"
                    options={
                        (referenceData?.[DEM_VOLTAGE_REF_KEY] as readonly (
                            | string
                            | number
                        )[]) || []
                    }
                    setValue={(
                        e_: SyntheticEvent<Element, Event>,
                        nv: string | number | null
                    ) => setFilterValue(nv, DEM_VOLTAGE_URL_PARAM)}
                />
                <TextField
                    label="System Reference"
                    value={localSystemRef}
                    placeholder="Type to search..."
                    onChange={(event) => {
                        setLocalSystemRef(event.target.value)
                        setFilterFromTextfield(
                            event.target.value,
                            DEMAND_DEVICE_SYSTEM_REF_KW
                        )
                    }}
                />
            </FilterPanelFrame>
        </Grid>
    )
}

export default DemandDeviceSearch
